import * as SentryVue from '@sentry/vue';
import type {ErrorEvent, EventHint} from '@sentry/core';
import {setTag} from '@sentry/core';
import {
    createDualFeedTransport,
    createEventThrottle,
    deniedUrls,
    fingerprintNonErrorRejection,
    ignoredErrorMessages,
    isSensitiveUrl,
    pinnedPrivacyOptions,
    pinnedReplayOptions,
    pinnedVueIntegrationOptions,
    replayEventScrubbingIntegration,
    scrubBreadcrumb,
    scrubEvent,
    scrubSpan,
} from '@deskly/observability';
import {environment} from '@/config/environment';
import type {App, Plugin} from 'vue';
import {isNavigationFailure, useRouter} from 'vue-router';
import type {Router} from 'vue-router';
import {useToast} from '@/shared/components/toast/useToast';

export const sentryPlugin = {
    install(app) {
        addErrorHandling(app);
        app.runWithContext(() => {
            const router = useRouter();
            const replay = SentryVue.replayIntegration(pinnedReplayOptions);
            const recordsReplay = !isSensitiveUrl(window.location.href);
            const options = getInitOptions(app, router, recordsReplay ? [replay] : []);

            if (__IS_HYBRID_APP__) {
                import('@sentry/capacitor').then(async SentryCapacitor => {
                    //@ts-ignore
                    SentryCapacitor.init(options, SentryVue.init);

                    const {CapacitorUpdater} = await import('@capgo/capacitor-updater');
                    const {bundle} = await CapacitorUpdater.current();

                    // Set tag for native errors
                    SentryCapacitor.setTag('capgo.bundle_id', bundle.version);

                    // Set tag for JS errors
                    setTag('capgo.bundle_id', bundle.version);
                });
            } else {
                SentryVue.init(options);
            }

            keepReplayOffSensitivePages(router, replay, recordsReplay);
        });

        window.addEventListener('vite:preloadError', () => {
            if (!sessionStorage.getItem('preload-retry')) {
                sessionStorage.setItem('preload-retry', '1');
                window.location.reload();
            }
        });
    },
} satisfies Plugin;

type SentryVueInitOptions = NonNullable<Parameters<typeof SentryVue.init>[0]>;

// The backend tracks these responses itself, and a 4xx payload is what the user just typed.
const FIRST_CLIENT_ERROR_STATUS = 400;
const INTERNAL_SERVER_ERROR_STATUS = 500;

const stackTraceSentryExceptions = [/_getMapPanePos/];

const isThrottled = createEventThrottle();

type ReplayIntegration = ReturnType<typeof SentryVue.replayIntegration>;

function getInitOptions(
    app: SentryVueInitOptions['app'],
    router: ReturnType<typeof useRouter>,
    replayIntegrations: ReplayIntegration[]
): SentryVueInitOptions & {enableCaptureFailedRequests: boolean} {
    const dsn = environment.VITE_SENTRY_DSN_JS;
    // Capacitor brings its own transport, so the hybrid app stays single-feed.
    const secondaryDsn = __IS_HYBRID_APP__ ? undefined : environment.VITE_SENTRY_DSN_JS_SECONDARY;

    return {
        ...pinnedPrivacyOptions,
        // Read by SentryCapacitor only; the native SDKs default it on.
        enableCaptureFailedRequests: false,
        app,
        dsn,
        transport: createDualFeedTransport(dsn, secondaryDsn),
        environment: environment.VITE_SENTRY_ENVIRONMENT,
        release: environment.VITE_APP_VERSION,
        integrations: [
            SentryVue.browserTracingIntegration({router}),
            SentryVue.vueIntegration({
                ...pinnedVueIntegrationOptions,
                tracingOptions: {
                    trackComponents: true,
                    hooks: ['create', 'mount', 'activate', 'destroy'],
                },
            }),
            replayEventScrubbingIntegration,
            ...replayIntegrations,
        ],
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.5,
        tracesSampleRate: environment.VITE_SENTRY_TRACING_SAMPLE_RATE,
        ignoreErrors: ignoredErrorMessages,
        denyUrls: deniedUrls,
        beforeBreadcrumb: scrubBreadcrumb,
        beforeSendTransaction: scrubEvent,
        beforeSendSpan: scrubSpan,
        beforeSend(event: ErrorEvent, hint: EventHint) {
            if (shouldDropEvent(event, hint)) {
                return null;
            }

            fingerprintNonErrorRejection(event, hint);

            return isThrottled(event) ? null : scrubEvent(event);
        },
    };
}

function keepReplayOffSensitivePages(
    router: Router,
    replay: ReplayIntegration,
    isRegistered: boolean
): void {
    let registered = isRegistered;
    let stopped = false;

    router.beforeEach(to => {
        if (registered && !stopped && isSensitiveUrl(to.fullPath)) {
            stopped = true;
            void replay.stop();
        }
    });

    router.afterEach(() => {
        if (
            registered ||
            stopped ||
            isSensitiveUrl(window.location.href) ||
            !SentryVue.getClient()
        ) {
            return;
        }

        registered = true;
        SentryVue.addIntegration(replay);
    });
}

function shouldDropEvent(event: ErrorEvent, hint: EventHint): boolean {
    const error = hint?.originalException;

    if (!error) {
        return false;
    }

    // DOM Event rejections (e.g. a <script> failing to load) carry no
    // stacktrace and nothing actionable - drop them instead of letting
    // each one spawn its own issue.
    if (error instanceof Event) {
        return true;
    }

    if (isBackendTrackedStatus((error as {status?: unknown}).status)) {
        return true;
    }

    if (isNavigationFailure(error as Error)) {
        return true;
    }

    const stackTrace = getStackTrace(event);

    return Boolean(stackTrace && stackTraceSentryExceptions.some(regex => regex.test(stackTrace)));
}

function isBackendTrackedStatus(status: unknown): boolean {
    if (typeof status !== 'number') {
        return false;
    }

    return status >= FIRST_CLIENT_ERROR_STATUS && status <= INTERNAL_SERVER_ERROR_STATUS;
}

function getStackTrace(event: ErrorEvent) {
    try {
        return event.exception?.values
            ?.map(exception => exception.stacktrace?.frames?.map(frame => frame.function)?.join(''))
            ?.filter(Boolean)
            ?.join('');
    } catch {
        return null;
    }
}

function addErrorHandling(app: App) {
    // Hide console logs in prod
    if (!import.meta.env.DEV) {
        app.config.errorHandler = _ => {};
        return;
    }

    // Use default error handler -> throw errors
    if (environment.VITE_DEV_ERROR_HANDLING === 'throw') {
        return;
    }

    app.config.errorHandler = error => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (environment.VITE_DEV_ERROR_HANDLING === 'alert') {
            useToast().error('A development error occurred.', {detail: error});
        }
    };
}
